import { useMutation } from '@apollo/client';
import type { DeleteDepartmentTypesMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { DeleteDepartmentTypesDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { evictField } from '@/utils/graphqlUtils';

import { useDeleteDepartmentTypesTRPC } from './useDeleteDepartmentTypesTRPC';

export const useDeleteDepartmentTypes = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');

  // GraphQL mutation
  const [deleteDepartmentTypesGraphQL, graphqlState] = useMutation(
    DeleteDepartmentTypesDocument,
    {
      update: (cache) => {
        evictField(cache, 'department_type');
      },
    }
  );

  // tRPC mutation
  const trpcMutation = useDeleteDepartmentTypesTRPC();

  const deleteDepartmentTypes = async (variables: {
    Ids: string[];
  }): Promise<DeleteDepartmentTypesMutation> => {
    if (trpcEnabled) {
      return trpcMutation.deleteDepartmentTypes(variables);
    }

    const result = await deleteDepartmentTypesGraphQL({
      variables: { Ids: variables.Ids },
    });
    if (!result.data) {
      throw new Error('Failed to delete department types');
    }

    return result.data;
  };

  if (trpcEnabled) {
    return {
      deleteDepartmentTypes,
      loading: trpcMutation.loading,
      error: trpcMutation.error,
      data: trpcMutation.data,
    };
  }

  return {
    deleteDepartmentTypes,
    loading: graphqlState.loading,
    error: graphqlState.error,
    data: graphqlState.data,
  };
};
